import {
  useEffect,
  useCallback,
  useContext,
  Dispatch,
  SetStateAction,
} from 'react';
import { MessageType, SessionType } from 'models/Message';
import uniqBy from 'lodash/uniqBy';
import bus, { CallbackBusType, StandardEventTypes } from 'modules/bus';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';
import MessagingContext from '../../../providers/Messaging/Messaging.context';
import { MessageCreatedEventData } from 'services/socketService/socketEventData';

type ChatListeners = {
  setMessages?: Dispatch<SetStateAction<MessageType[]>>;
  onMessageArrived?: (message: MessageCreatedEventData) => void;
  onMessageSent?: (data: CallbackBusType<MessageType>) => void;
  onSessionPicked?: (data: CallbackBusType<SessionType>) => void;
};

export default function useChatListener(config: ChatListeners) {
  const { setMessages, onMessageSent, onMessageArrived, onSessionPicked } =
    config;

  const { sessionId } = useContext(MessagingContext);

  const onNewMessage = useCallback(
    ({ message, session }: MessageCreatedEventData) => {
      if (!sessionId) return;

      // the server echoes the sender's own message too, so dedup by _id to
      // avoid adding a second copy of a message already inserted on send
      if (setMessages && sessionId === message.sessionId)
        setMessages((oldMessages) => uniqBy([message, ...oldMessages], '_id'));

      onMessageArrived?.({ message, session });
    },
    [sessionId, onMessageArrived, setMessages],
  );

  useSocketEvent(LISTEN_EVENTS.MESSAGE_CREATED, onNewMessage);

  useEffect(() => {
    if (onMessageSent)
      bus.addEventListener(StandardEventTypes.MESSAGE_SENT, onMessageSent);

    if (onSessionPicked)
      bus.addEventListener(StandardEventTypes.SESSION_PICKED, onSessionPicked);

    return () => {
      if (onMessageSent)
        bus.removeEventListener(StandardEventTypes.MESSAGE_SENT, onMessageSent);

      if (onSessionPicked)
        bus.removeEventListener(
          StandardEventTypes.SESSION_PICKED,
          onSessionPicked,
        );
    };
  }, [onMessageSent, onNewMessage, onSessionPicked]);
}
